import React from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import classNames from 'classnames';
import EBPropTypes from 'js-utils/prop-types';
import {HERO_STYLE, IMAGE_STYLE_FLUID} from 'eventbrite_design_system/mediaCardContent/constants';

import {setWindowLocation} from 'js-utils/http';
import ArticleCard from './ArticleCard';
import SearchEventCard from './SearchEventCard';
import BucketHeader from './BucketHeader';
import SeeMoreButton from './SeeMoreButton';
import {
    FEATURED_TEMPLATE,
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    CARD_BIG,
    ARTICLE_TEMPLATE,
    SHARE_OPTIONS_PROPTYPE,
    GA_VIEW_MORE_ACTION,
    GA_CITY_BROWSE_CATEGORY,
    EXPERIMENTS_SHAPE_PROP_TYPE,
} from '../constants';


const _getCardsBig = (
    isSmallCity,
    cards,
    onSaveEvent,
    shareOptions,
    isAuthenticated,
    onTrackClick,
    onTrackClickInternal,
    trackGAEvent,
    experiments,
    featureFlags,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
) => {
    const cardsArrayLength = cards.length;

    return cards.map(({id, ...cardProps}, index) => {
        const shouldTrimOddCard = cardsArrayLength % 2 === 1 && index === cardsArrayLength - 1 && !isSmallCity;
        const classes = classNames(
            'eds-g-cell',
            'eds-g-cell--has-overflow',
            'eds-g-cell-md-4-12',
            'eds-g-cell-sm-6-12',
            'eds-g-cell-1-1',
            {
                //hide extraneous cards
                'eds-show-up-sm': index > 2 && !isSmallCity,
                //trim odd card so doesnt look wierd on medium breakpoints
                'eds-hide-sm eds-hide-sw eds-hide-mn': shouldTrimOddCard,
            },
        );

        return (
            <div className={classes} key={id}>
                <SearchEventCard
                    id={id}
                    onSaveEvent={onSaveEvent}
                    shareOptions={shareOptions}
                    isAuthenticated={isAuthenticated}
                    trackClick={onTrackClick.bind(null, {id})}
                    trackClickInternal={onTrackClickInternal.bind(null, index)}
                    trackGAEvent={trackGAEvent}
                    experiments={experiments}
                    featureFlags={featureFlags}
                    userCollections={userCollections}
                    onAddOrRemoveEventToCollection={onAddOrRemoveEventToCollection}
                    onCreateCollection={onCreateCollection}
                    {...cardProps}
                />
            </div>
        );
    });
};

const _getCardsSmall = (
    cards,
    isMobile,
    onSaveEvent,
    shareOptions,
    isAuthenticated,
    onTrackClick,
    onTrackClickInternal,
    trackGAEvent,
    experiments,
    featureFlags,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
) => {
    const classes = classNames({
        'eds-g-cell eds-g-cell-3-12 eds-g-cell--has-overflow': !isMobile,
        'small-card-mobile': isMobile,
    });

    return cards.map(({id, ...cardProps}, index) => (
        <div className={classes} key={id}>
            <SearchEventCard
                id={id}
                onSaveEvent={onSaveEvent}
                shareOptions={shareOptions}
                isAuthenticated={isAuthenticated}
                trackGAEvent={trackGAEvent}
                trackClick={onTrackClick.bind(null, {id})}
                trackClickInternal={onTrackClickInternal.bind(null, index)}
                experiments={experiments}
                featureFlags={featureFlags}
                userCollections={userCollections}
                onAddOrRemoveEventToCollection={onAddOrRemoveEventToCollection}
                onCreateCollection={onCreateCollection}
                {...cardProps}
            />
        </div>
    ));
};

const CardGroup = ({
    cards,
    linkUrl,
    onSaveEvent,
    shareOptions,
    isAuthenticated,
    onTrackClick,
    title,
    onTrackClickInternal,
    trackGAEvent,
    experiments,
    featureFlags,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
}) => (
    <div>
        <div className="eds-show-up-md">
            {_getCardsSmall(
                cards,
                false,
                onSaveEvent,
                shareOptions,
                isAuthenticated,
                onTrackClick,
                onTrackClickInternal,
                trackGAEvent,
                experiments,
                featureFlags,
                userCollections,
                onAddOrRemoveEventToCollection,
                onCreateCollection,
            )}
        </div>
        <div className="eds-show-down-mn">
            <div className="mobile-card-carousel eds-l-mar-bot-4">
                {_getCardsSmall(
                    cards,
                    true,
                    onSaveEvent,
                    shareOptions,
                    isAuthenticated,
                    onTrackClick,
                    onTrackClickInternal,
                    trackGAEvent,
                    experiments,
                    featureFlags,
                    userCollections,
                    onAddOrRemoveEventToCollection,
                    onCreateCollection,
                )}
            </div>
            <div className="eds-show-down-sn eds-align--center">
                <div className="eds-g-cell">
                    <SeeMoreButton
                        linkUrl={linkUrl}
                        trackClick={onTrackClick}
                        title={title}
                        gaAction={GA_VIEW_MORE_ACTION}
                    />
                </div>
            </div>
        </div>
    </div>
);

const BigCardGroup = ({
    cards,
    linkUrl,
    onSaveEvent,
    shareOptions,
    isAuthenticated,
    onTrackClick,
    isSmallCity,
    title,
    onTrackClickInternal,
    trackGAEvent,
    experiments,
    featureFlags,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
}) => (
    <div className="eds-g-group">
        {_getCardsBig(
            isSmallCity,
            cards,
            onSaveEvent,
            shareOptions,
            isAuthenticated,
            onTrackClick,
            onTrackClickInternal,
            trackGAEvent,
            experiments,
            featureFlags,
            userCollections,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
        )}
        <div className="eds-show-down-sn eds-align--center">
            <div className="eds-g-cell">
                <SeeMoreButton
                    linkUrl={linkUrl}
                    trackClick={onTrackClick}
                    title={title}
                    gaAction={GA_VIEW_MORE_ACTION}
                />
            </div>
        </div>
    </div>
);

const _getRemainingArticleCards = (cards, trackGAEventOnPageUnload, showSummary, classes = '') => (
    cards.map(({id, name, ...cardProps}) => {
        let component = null;

        const props = showSummary ? cardProps : (omit(cardProps, 'summary'));

        if (name) {
            component = (
                <div className={classes} key={id}>
                    <ArticleCard
                        id={id}
                        name={name}
                        trackGAEventOnPageUnload={trackGAEventOnPageUnload}
                        {...props}
                    />
                </div>
            );
        }

        return component;
    })
);

const FeaturedCardGroup = ({cards, trackGAEventOnPageUnload}) => {
    //Assumes that API response will have 3 card responses
    const [
        primaryCard,
        ...remainingCards
    ] = cards;

    return (
        <div className="eds-g-group">
            <div className="eds-show-up-mn featured-article-bucket-container">
                <div className="eds-g-cell eds-g-cell-1-1 eds-g-cell-md-8-12">
                    <ArticleCard
                        style={HERO_STYLE}
                        imageStyle={IMAGE_STYLE_FLUID}
                        trackGAEventOnPageUnload={trackGAEventOnPageUnload}
                        {...primaryCard}
                    />
                </div>
                <div className="eds-g-cell eds-g-cell-1-1 eds-g-cell-md-4-12 eds-show-up-mn">
                    {_getRemainingArticleCards(remainingCards, trackGAEventOnPageUnload, false, 'eds-g-cell eds-g-cell-6-12 eds-g-cell-md-1-1')}
                </div>
            </div>
            <div className="eds-show-down-sw">
                <div className="eds-g-cell eds-g-cell-1-1">
                    {_getRemainingArticleCards(cards, trackGAEventOnPageUnload, true)}
                </div>
            </div>
        </div>
    );
};

const ArticleCardGroup = ({cards: [bigCard, cardOne, cardTwo, ...cards], linkUrl, onTrackClick, trackGAEventOnPageUnload, title}) => (
    <div className="eds-g-group">
        <div className="eds-show-up-mn article-bucket-container">
            <div className="eds-g-cell eds-g-cell-1-1 eds-g-cell-mw-6-12 eds-l-pad-right-2">
                <ArticleCard
                    style={HERO_STYLE}
                    imageStyle={IMAGE_STYLE_FLUID}
                    {...bigCard}
                    trackGAEventOnPageUnload={trackGAEventOnPageUnload}
                />
            </div>
            <div className="eds-g-cell eds-g-cell-1-1 eds-g-cell-mw-6-12">
                {_getRemainingArticleCards([cardOne, cardTwo, ...cards], trackGAEventOnPageUnload, false, 'eds-g-cell eds-g-cell-sw-6-12')}
            </div>
        </div>
        <div className="eds-show-down-sw">
            <div className="eds-g-cell eds-g-cell-1-1">
                {_getRemainingArticleCards([bigCard, cardOne, cardTwo], trackGAEventOnPageUnload, true)}
            </div>
            <div className="eds-l-pad-top-6">
                <div className="eds-show-down-sn eds-align--center">
                    <div className="eds-g-cell">
                        <SeeMoreButton
                            linkUrl={linkUrl}
                            trackClick={onTrackClick}
                            title={title}
                            gaAction={GA_VIEW_MORE_ACTION}
                        />
                    </div>
                </div>
            </div>
        </div>

    </div>
);

const CARD_GROUP_MAP = {
    [ARTICLE_TEMPLATE]: ArticleCardGroup,
    [FEATURED_TEMPLATE]: FeaturedCardGroup,
    [LARGE_CARD_TEMPLATE_3]: BigCardGroup,
    [LARGE_CARD_TEMPLATE_6]: BigCardGroup,
    [CARD_TEMPLATE_4]: CardGroup,
    [CARD_TEMPLATE_8]: CardGroup,
    [CARD_BIG]: BigCardGroup,
};

export default class CardBucket extends React.PureComponent {
    static propTypes = {
        /*
        * Array of preformatted objects that will represent that cards within the bucket
        */
        content: PropTypes.arrayOf(PropTypes.object).isRequired,
        /*
        * The manner in which this CardBucket should display its content
        */
        template: PropTypes.string.isRequired,
        /*
        * Share options
        */
        shareOptions: SHARE_OPTIONS_PROPTYPE.isRequired,
        /*
        * The name of the given bucket ie. "Upcoming Music Events"
        */
        name: EBPropTypes.translation,
        /*
        * The url clicking "See More" should connect the user to
        */
        linkUrl: PropTypes.string,
        /*
        * Callback for handling click on save event
        */
        onSaveEvent: PropTypes.func,
        /*
        * Is user authenticated?
        */
        isAuthenticated: PropTypes.bool,
        /*
        * The key of the associated bucket
        */
        bucketKey: PropTypes.string,
        /*
         * Tracking callback for event clicks
         */
        onTrackEventClickInternal: PropTypes.func,
        /*
        * Track GA Events from state
        */
        trackGAEvent: PropTypes.func,
        /*
        * Track GA Events from state
        */
        trackGAEventOnPageUnload: PropTypes.func,


        experiments: EXPERIMENTS_SHAPE_PROP_TYPE,
    }

    _handleTrackClick = ({url, id}, action) => {
        const {bucketKey, trackGAEventOnPageUnload} = this.props;

        if (bucketKey) {
            trackGAEventOnPageUnload({
                category: GA_CITY_BROWSE_CATEGORY,
                action,
                label: bucketKey,
                dimensions: {
                    eventId: id,
                },
            });
        }

        /* Need to fire tracking action on the seemore, so necessary to
        manually set windowlocation rather that relying on the link. */
        if (url) {
            setWindowLocation(url);
        }
    }

    _handleTrackClickInternal = (index, id, ticketsBy) => {
        const {bucketKey, onTrackEventClickInternal} = this.props;

        const trackingData = {
            clickRank: index,
            navigation: `city_browse.${bucketKey}`,
            itemId: id,
        };

        onTrackEventClickInternal(trackingData, ticketsBy);
    }

    render() {
        const {
            content,
            name,
            template,
            linkUrl,
            onSaveEvent,
            shareOptions,
            isAuthenticated,
            experiments,
            featureFlags,
            trackGAEvent,
            trackGAEventOnPageUnload,
            bucketKey,
            userCollections,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
        } = this.props;

        const DynamicCardGroup = CARD_GROUP_MAP[template];
        const isSmallCity = template === CARD_BIG;

        return (
            <div>
                <BucketHeader
                    title={name}
                    linkUrl={linkUrl}
                    template={template}
                    trackGAEvent={trackGAEvent}
                    bucketKey={bucketKey}
                />
                <DynamicCardGroup
                    cards={content}
                    linkUrl={linkUrl}
                    onSaveEvent={onSaveEvent}
                    shareOptions={shareOptions}
                    isAuthenticated={isAuthenticated}
                    onTrackClick={this._handleTrackClick}
                    onTrackClickInternal={this._handleTrackClickInternal}
                    trackGAEvent={trackGAEvent}
                    trackGAEventOnPageUnload={trackGAEventOnPageUnload}
                    isSmallCity={isSmallCity}
                    title={name}
                    experiments={experiments}
                    featureFlags={featureFlags}
                    userCollections={userCollections}
                    onAddOrRemoveEventToCollection={onAddOrRemoveEventToCollection}
                    onCreateCollection={onCreateCollection}
                />
            </div>
        );
    }
}
